import { useEffect, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { loadWorldFlagsLib } from "@/extension/utils/lazy-lib";

/**
 * Stateful lazy boundary over react-world-flags' <Flag/> component.
 *
 * react-world-flags is a single ~3.7MB module (the component + an inline map of
 * every country's flag SVG). It is rendered by the always-visible top-header
 * language switcher and the transcription language picker, so shipping it in
 * app.js means those 3.7MB are parsed at content-script startup. This boundary
 * loads the Flag component on demand from the lazy/world-flags.js WAR bundle
 * (which shares app.js's React via the __BT_REACT__ bridge — see
 * src/extension/utils/lazy-lib.ts loadWorldFlagsLib).
 *
 * Why a stateful wrapper instead of React.lazy + Suspense (as the calendar /
 * date-range splits use): the flag renders in MANY scattered places at once (a
 * dropdown lists one per locale, plus the always-visible trigger) with no
 * existing Suspense boundary around them. A stateful loader lets each instance
 * paint a correctly-sized placeholder box and then swap in the real flag once
 * the shared (memoized) loader resolves — zero layout shift, no dropdown-wide
 * suspense flash, and no risk of suspending an unexpected ancestor. It still
 * reuses the exact same lazy infra (loadWorldFlagsLib -> loadLazyComponentBundle
 * -> the React-identity bridge + shims + WAR entry).
 *
 * The loaded component is cached module-side so only the first mounted flag pays
 * the load; every later flag renders synchronously.
 */

export interface LazyWorldFlagProps {
  code: string;
  height?: number | string;
  width?: number | string;
  fallback?: ReactNode;
  alt?: string;
  style?: CSSProperties;
  className?: string;
}

// Shared across every LazyWorldFlag instance in this document: once the first
// flag resolves the component, later flags render it synchronously.
let cachedFlagComponent: any = null;

const LazyWorldFlag: React.FC<LazyWorldFlagProps> = (props) => {
  // MUST wrap in an initializer fn: cachedFlagComponent is itself a function
  // (the Flag component). `useState(cachedFlagComponent)` would make React call
  // it as a LAZY STATE INITIALIZER — Flag() runs with undefined props →
  // "reading 'code'" crash on every flag after the first (R16/R17 root cause).
  const [FlagComponent, setFlagComponent] = useState<any>(() => cachedFlagComponent);

  useEffect(() => {
    if (cachedFlagComponent) return;
    let mounted = true;
    loadWorldFlagsLib()
      .then((Flag) => {
        cachedFlagComponent = Flag;
        // Store the component itself in state via the functional updater so
        // React doesn't treat the function component as a lazy state initializer.
        if (mounted) setFlagComponent(() => Flag);
      })
      .catch(() => {
        /* loadWorldFlagsLib already logs + guards its own fallback */
      });
    return () => {
      mounted = false;
    };
  }, []);

  if (!FlagComponent) {
    // Sized placeholder so the flag popping in causes no layout shift. Uses the
    // caller's width/height/style so it occupies the exact flag footprint.
    const { width, height, style } = props;
    return (
      <span
        aria-hidden="true"
        style={{
          display: "inline-block",
          width,
          height,
          ...style,
        }}
      />
    );
  }

  return <FlagComponent {...props} />;
};

export default LazyWorldFlag;
